import React, { useEffect, useRef, useState } from 'react';
import QRCode from 'qrcodejs2-fix';
import {
  Download
} from 'lucide-react';

/**
 * QRCodeGenerator Component
 * 
 * Generates QR codes with customizable options and export functionality
 * 
 * @param {string} url - The URL to encode in the QR code
 * @param {string} title - Title displayed above the QR code
 * @param {string} filename - Base filename for downloads (without extension)
 * @param {number} size - Size of the QR code in pixels (default: 256)
 * @param {string} colorDark - Dark color for QR code (default: '#000000')
 * @param {string} colorLight - Light color for QR code (default: '#ffffff')
 * @param {string} containerId - Unique ID for the container element
 */
function QRCodeGenerator({ 
  url, 
  title = 'QR Code', 
  filename = 'qrcode',
  size = 256,
  colorDark = '#000000',
  colorLight = '#ffffff',
  containerId = 'qrcode-container'
}) {
  const qrContainerRef = useRef(null);
  const qrCodeRef = useRef(null);
  const [isGenerating, setIsGenerating] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!url) {
      setError('URL is required');
      setIsGenerating(false);
      return;
    }

    let isMounted = true;

    // Small delay to ensure the container is rendered
    const timer = setTimeout(() => {
      if (!isMounted) return;
      
      const container = qrContainerRef.current;
      if (!container) return;

      // Clear any existing QR code
      if (qrCodeRef.current) {
        try {
          qrCodeRef.current.clear();
        } catch (e) {
          // Ignore errors if already cleared
        }
        qrCodeRef.current = null;
      }
      
      // Create a new div for QRCode to own exclusively
      const qrDiv = document.createElement('div');
      container.appendChild(qrDiv);
      
      try {
        qrCodeRef.current = new QRCode(qrDiv, {
          text: url,
          width: size,
          height: size,
          colorDark: colorDark,
          colorLight: colorLight,
          correctLevel: QRCode.CorrectLevel.H
        });
        if (isMounted) {
          setIsGenerating(false);
          setError(null);
        }
      } catch (err) {
        console.error('Error generating QR code:', err);
        if (isMounted) {
          setError('Failed to generate QR code');
          setIsGenerating(false);
        }
      }
    }, 100);

    return () => {
      isMounted = false;
      clearTimeout(timer);
      
      // Only clean up the QR code instance
      // Let React handle DOM cleanup to prevent conflicts
      if (qrCodeRef.current) {
        try {
          qrCodeRef.current.clear();
        } catch (e) {
          // Ignore errors on cleanup
        }
        qrCodeRef.current = null;
      }
    };
  }, [url, size, colorDark, colorLight]);

  /**
   * Download QR code as PNG
   */
  const downloadPNG = () => {
    const canvas = qrContainerRef.current?.querySelector('canvas');
    if (canvas) {
      const url = canvas.toDataURL('image/png');
      const link = document.createElement('a');
      link.download = `${filename}.png`;
      link.href = url;
      link.click();
    }
  };

  /**
   * Download QR code as SVG
   * Converts the canvas to SVG format
   */
  const downloadSVG = () => {
    const canvas = qrContainerRef.current?.querySelector('canvas');
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
    const pixels = imageData.data;
    
    // Create SVG with paths for each dark pixel
    let svgContent = `<?xml version="1.0" encoding="UTF-8"?>
<svg xmlns="http://www.w3.org/2000/svg" version="1.1" width="${canvas.width}" height="${canvas.height}" viewBox="0 0 ${canvas.width} ${canvas.height}">
  <rect width="100%" height="100%" fill="${colorLight}"/>
  <g fill="${colorDark}">`;

    // Scan pixels and create rectangles for dark pixels
    for (let y = 0; y < canvas.height; y++) {
      for (let x = 0; x < canvas.width; x++) {
        const index = (y * canvas.width + x) * 4;
        const r = pixels[index];
        const g = pixels[index + 1];
        const b = pixels[index + 2];
        
        // Check if pixel is dark (close to colorDark)
        if (r < 128 && g < 128 && b < 128) {
          svgContent += `\n    <rect x="${x}" y="${y}" width="1" height="1"/>`;
        }
      }
    }

    svgContent += '\n  </g>\n</svg>';

    // Create download link
    const blob = new Blob([svgContent], { type: 'image/svg+xml' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.download = `${filename}.svg`;
    link.href = url;
    link.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="text-center">
      {title && (
        <h4 className="text-lg font-semibold text-gray-800 mb-4">{title}</h4>
      )}
      
      <div className="mb-6 flex justify-center">
        <div 
          className="bg-white p-4 rounded-lg shadow-md inline-block relative"
          style={{ width: size + 32, height: size + 32 }}
        >
          {isGenerating && (
            <div className="absolute inset-0 flex items-center justify-center bg-white bg-opacity-75 rounded-lg z-10">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-teal-600"></div>
            </div>
          )}
          {error && (
            <div className="absolute inset-0 flex items-center justify-center bg-red-50 rounded-lg p-4 z-10">
              <p className="text-red-600 text-sm">{error}</p>
            </div>
          )}
          {/* QR code container - managed outside React */}
          <div 
            ref={qrContainerRef}
            id={containerId}
          />
        </div>
      </div>
      
      {url && (
        <div className="mb-6">
          <a 
            href={url}
            target="_blank"
            rel="noopener noreferrer"
            className="text-sm text-teal-600 hover:text-teal-700 underline font-medium break-all"
          >
            {url}
          </a>
        </div>
      )}
      
      {!isGenerating && !error && (
        <div className="flex justify-center space-x-3">
          <button
            type="button"
            onClick={downloadPNG}
            className="inline-flex items-center px-4 py-2 bg-gray-800 text-white rounded-lg hover:bg-gray-900 transition-colors"
          >
            <Download className="w-5 h-5 mr-2" />
            Download PNG
          </button>
          
          <button
            type="button"
            onClick={downloadSVG}
            className="inline-flex items-center px-4 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-colors"
          >
            <Download className="w-5 h-5 mr-2" />
            Download SVG
          </button>
        </div>
      )}
    </div>
  );
}

export default QRCodeGenerator;

